import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, MessageSquare, X, Plus, ArrowLeft } from './Icons';
import ConversationItem from './ConversationItem';
import { useDM } from '../../context/DMContext';

const ChatHistoryList = ({ 
  onConversationSelect, 
  onNewConversation, 
  onClose,
  activeConversationId = null,
  onGlobalChatSelect
}) => {
  const { t } = useTranslation();
  const { conversations, loading } = useDM();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const debounceTimeoutRef = useRef(null);

  // Constants for better maintainability
  const MOBILE_BREAKPOINT = 768;
  const DEBOUNCE_DELAY = 150;

  // Debounce search query for better performance
  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, DEBOUNCE_DELAY);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // Pre-process conversations with normalized search data for performance
  const normalizedConversations = useMemo(() => {
    return conversations.map(conversation => {
      // Handle both possible data structures:
      // 1. conversation.other_participant.* (legacy structure)
      // 2. conversation.other_* (current structure from recent_dm_conversations view)
      const participant = conversation.other_participant;
      const displayName = participant?.display_name || conversation.other_display_name || '';
      const username = participant?.username || conversation.other_username || '';
      
      
      return {
        ...conversation,
        searchData: {
          displayNameLower: displayName.toLowerCase(),
          usernameLower: username.toLowerCase(),
          combinedSearch: `${displayName} ${username}`.toLowerCase()
        }
      };
    });
  }, [conversations]);

  // Filter conversations based on debounced search query (player names only)
  const filteredConversations = useMemo(() => {
    if (!debouncedQuery.trim()) return normalizedConversations;
    
    const query = debouncedQuery.toLowerCase().trim();
    
    // Use more efficient search with pre-normalized data
    return normalizedConversations.filter(conversation => {
      const { searchData } = conversation;
      return searchData.displayNameLower.includes(query) || 
             searchData.usernameLower.includes(query);
    });
  }, [normalizedConversations, debouncedQuery]);

  // Optimized event handlers with useCallback
  const handleConversationClick = useCallback((conversation) => {
    onConversationSelect(conversation);
    // On mobile, close the history view after selecting a conversation
    if (window.innerWidth < MOBILE_BREAKPOINT) {
      onClose?.();
    }
  }, [onConversationSelect, onClose]);

  const handleGlobalChatClick = useCallback(() => {
    onGlobalChatSelect?.();
    // On mobile, close the history view after selecting global chat
    if (window.innerWidth < MOBILE_BREAKPOINT) {
      onClose?.();
    }
  }, [onGlobalChatSelect, onClose]);

  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
    setDebouncedQuery(''); // Clear immediately for better UX
  }, []);

  return (
    <div className="flex flex-col h-full bg-white dark:bg-zinc-900 border border-zinc-700 rounded-lg shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-zinc-700 bg-gray-50 dark:bg-zinc-800 rounded-t-lg">
        <div className="flex items-center space-x-3">
          {/* Mobile back button */}
          <button
            onClick={onClose}
            className="sm:hidden p-1 hover:bg-gray-200 dark:hover:bg-zinc-700 rounded-lg transition-colors"
            aria-label={t('common.goBack')}
          >
            <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          </button>
          
          <div className="flex items-center space-x-2">
            <MessageSquare className="w-6 h-6 text-blue-500" />
            <h2 className="text-lg font-bold text-white">
              {t('chat.conversations', { defaultValue: 'Conversations' })}
            </h2>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {/* New conversation button */}
          <button
            onClick={onNewConversation}
            className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors flex items-center space-x-1"
            title={t('chat.newConversation', { defaultValue: 'New Conversation' })}
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline text-sm font-medium">
              {t('chat.new', { defaultValue: 'New' })}
            </span>
          </button>

          {/* Desktop close button */}
          <button
            onClick={onClose}
            className="hidden sm:block p-2 hover:bg-gray-200 dark:hover:bg-zinc-700 rounded-lg transition-colors"
            aria-label={t('common.close')}
          >
            <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="p-3 border-b border-zinc-700">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder={t('chat.searchPlayers', { defaultValue: 'Search players...' })}
            value={searchQuery}
            onChange={handleSearchChange}
            className="w-full pl-10 pr-4 py-2 bg-gray-100 dark:bg-zinc-800 border border-gray-200 dark:border-zinc-600 rounded-lg text-sm text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
          />
          {/* Clear search button */}
          {searchQuery && (
            <button
              onClick={handleClearSearch}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-200 dark:hover:bg-zinc-700 rounded-full transition-colors"
              aria-label={t('common.clear')}
            >
              <X className="w-3 h-3 text-gray-400" />
            </button>
          )}
        </div>
      </div>

      {/* Conversations List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center p-8">
            <div className="w-6 h-6 border-2 border-[#31C590] border-t-transparent rounded-full animate-spin"></div>
            <span className="ml-3 text-gray-600 dark:text-gray-300">
              {t('common.loading')}
            </span>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-zinc-700">
            {/* Global Chat - Always visible unless searching */}
            {!searchQuery && (
              <div
                onClick={handleGlobalChatClick}
                className={`flex items-center p-4 hover:bg-gray-50 dark:hover:bg-zinc-800 cursor-pointer transition-colors ${
                  activeConversationId === 'global' ? 'bg-blue-50 dark:bg-blue-900/20 border-r-2 border-blue-500' : ''
                }`}
              >
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-3 flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {t('chat.globalChat', { defaultValue: 'Global Chat' })}
                    </h3>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t('chat.globalChatDesc', { defaultValue: 'Chat with all online players' })}
                  </p>
                </div>
              </div>
            )}
            
            {/* DM Conversations */}
            {filteredConversations.length > 0 && filteredConversations.map((conversation) => (
              <ConversationItem
                key={conversation.conversation_id}
                conversation={conversation}
                isActive={conversation.conversation_id === activeConversationId}
                onClick={() => handleConversationClick(conversation)}
              />
            ))}
            
            {/* No conversations message - only show if no DMs and no search */}
            {filteredConversations.length === 0 && searchQuery && (
              <div className="flex flex-col items-center justify-center p-8 text-center">
                <Search className="w-12 h-12 text-gray-400 mb-3" />
                <h3 className="text-lg font-medium text-white mb-2">
                  {t('chat.noSearchResults', { defaultValue: 'No conversations found' })}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                  {t('chat.noSearchResultsDesc', { 
                    defaultValue: 'Try searching with different keywords',
                    query: searchQuery 
                  })}
                </p>
                <button
                  onClick={handleClearSearch}
                  className="text-blue-500 hover:text-blue-600 text-sm font-medium"
                >
                  {t('common.clearSearch', { defaultValue: 'Clear search' })}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer with conversation count */}
      {filteredConversations.length > 0 && (
        <div className="p-3 border-t border-zinc-700 bg-gray-50 dark:bg-zinc-800 rounded-b-lg">
          <p className="text-xs text-gray-600 dark:text-gray-400 text-center">
            {searchQuery ? (
              t('chat.searchResults', { 
                count: filteredConversations.length,
                total: conversations.length,
                defaultValue: `${filteredConversations.length} of ${conversations.length} conversations`
              })
            ) : (
              t('chat.totalConversations', { 
                count: conversations.length,
                defaultValue: `${conversations.length} conversation${conversations.length === 1 ? '' : 's'}`
              })
            )}
          </p>
        </div>
      )}
    </div>
  );
};

export default ChatHistoryList;